import { Block, Check, CheckCircleOutline, VoiceOverOff } from '@mui/icons-material';
import BuildIcon from '@mui/icons-material/Build';
import FlashAutoIcon from '@mui/icons-material/FlashAuto';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogTitle,
    FormControlLabel,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography
} from '@mui/material';
import { action, computed, makeObservable, observable, reaction } from 'mobx';
import { inject, observer } from 'mobx-react';
import NumeroProcesso from '../NumeroProcesso';
import PainelFiltros from '../PainelFiltros';
import ReactComponent from '../ReactComponent';
import FiltroRapido from './FiltroRapido';
import OperacoesEmLoteSelecionar from './OperacoesEmLoteSelecionar';
import Cancel from '@mui/icons-material/Cancel';
import moment from 'moment';
import { blue, red } from '@mui/material/colors';
import SituacaoJulgamentoProcessoBusiness from '@/dominio/SituacaoJulgamentoProcessoBusiness';
import { MEIO_JULGAMENTO_PROCESSO } from '@/dominio/MeioJulgamentoProcesso';

/**
 * Estilo da borda das células da tabela.
 * @readonly
 * @constant {string}
 */
const ESTILO_BORDA = '1px solid rgba(224, 224, 224, 1)';

const ESTILO_CABECALHO_TABELA = {
    fontWeight: 'bold',
    fontSize: '18px',
    borderLeft: ESTILO_BORDA,
    borderTop: ESTILO_BORDA,
    p: 1
};
class ModalControlePermissaoSustentacao extends ReactComponent {
    dialogOpen = false;
    permitirSustentacao = false;
    permitirDestaque = false;

    constructor(props) {
        super(props);

        makeObservable(this, {
            dialogOpen: observable,
            permitirSustentacao: observable,
            permitirDestaque: observable,
            handleClickIcone: action,
            handleCloseDialog: action,
            processosPauta: computed,
            processosSalaSessao: computed,
            processos: computed,
            limparProcessoPesquisa: action,
            processosSelecionaveis: computed,
            processosSelecionados: computed,
            alterarPermissaoSustentacaoEmLote: action,
            mensagemLoteSemProcessos: computed,
            isJulgamentoExclusivamentePresencial: computed,
            handleSelecionarProcesso: action,
            handleChangePermitirSustentacao: action,
            handleChangePermitirDestaque: action
        });

        this.createReaction(
            reaction(
                () => this.dialogOpen,
                () => this.limparProcessoPesquisa()
            )
        );
    }

    handleClickIcone = () => {
        this.dialogOpen = true;
    };

    handleCloseDialog = () => {
        this.dialogOpen = false;
    };

    get processosPauta() {
        return (this.pautaJulgamentoStore.processosPautaSelecionadaFiltrados || []).filter((registro) =>
            this.filtrarProcessoPeloNumero(registro)
        );
    }

    get processosSalaSessao() {
        return (this.store.processosFiltrados || []).filter((registro) => this.filtrarProcessoPeloNumero(registro));
    }

    get processos() {
        return this.isPautaJulgamento ? this.processosPauta : this.processosSalaSessao;
    }

    limparProcessoPesquisa = () => {
        this.processoPesquisa = { numero: '', digito: '', ano: '', orgao: '', regional: '', vara: '' };
    };

    get processosSelecionaveis() {
        return this.processos.filter((registro) => registro.tipoJulgamento === MEIO_JULGAMENTO_PROCESSO.VIRTUAL.codigo);
    }

    get processosSelecionados() {
        return this.processosSelecionaveis.filter((registro) => this.store.isCheckedOperacoesEmLote(registro));
    }

    get isJulgamentoExclusivamentePresencial() {
        return SituacaoJulgamentoProcessoBusiness.isJulgamentoExclusivamentePresencial(
            this.sessaoStore?.sessaoSelecionada
        );
    }

    alterarPermissaoSustentacaoEmLote = async () => {
        if (this.processosSelecionados.length > 0) {
            await this.store.processarLote({
                titulo: 'Alterando permissão de sustentação oral eletrônica e pedido de destaque',
                qtdTotalProcessos: this.processosSelecionados.length,
                lotesProcessos: this.processosSelecionados,
                funcaoOperacao: (registro) =>
                    this.alterarPermissaoSustentacao(registro, this.permitirSustentacao, this.permitirDestaque)
            });
        } else {
            this.mensagemStore.abrirMensagemAlerta('Selecione um ou mais processos');
        }
    };

    async alterarPermissaoSustentacao(registro, permiteSustentacao, permiteDestaque) {
        let resultado = await this.processoService.alterarPermissaoSustentacao(
            registro?.sessaoId,
            registro?.processo?.numeroCompleto,
            permiteSustentacao,
            permiteDestaque
        );

        await this.sessaoStore.recarregarProcesso({
            sessaoId: registro?.sessaoId,
            processo: { numeroCompleto: registro?.processo?.numeroCompleto }
        });

        if (this.isPautaJulgamento) {
            this.pautaJulgamentoStore.incrementarProgressoLote();
        } else {
            this.salaSessaoStore.incrementarProgressoLote();
        }

        return resultado;
    }

    get mensagemLoteSemProcessos() {
        return 'Não há processos na sessão' + this.complementoMensagemFiltrando;
    }

    getAlertasProcesso = () => {
        return [];
    };

    handleSelecionarProcesso = (registro) => {
        this.store.toggleCheckedOperacoesEmLote(registro);
    };

    handleChangePermitirSustentacao = () => {
        this.permitirSustentacao = !this.permitirSustentacao;
    };

    handleChangePermitirDestaque = () => {
        this.permitirDestaque = !this.permitirDestaque;
    };

    renderCheckbox(registro) {
        return (
            <Checkbox
                checked={this.store.isCheckedOperacoesEmLote(registro)}
                onChange={this.handleSelecionarProcesso.bind(this, registro)}
            />
        );
    }

    renderMarcacaoManual(detalhes) {
        return (
            <Tooltip
                title={
                    <Typography>
                        <Box sx={{ display: 'block' }}>
                            Indicação manual realizada por {detalhes?.nomeUsuarioMarcacao} em{' '}
                            {moment(detalhes?.dataMarcacao, 'DD-MM-YYYY-HH-mm-ss').format('DD/MM/YYYY [às] HH:mm')}.
                        </Box>
                    </Typography>
                }
                placement="top"
            >
                <Stack
                    direction={'row'}
                    alignItems={'center'}
                    alignContent={'center'}
                    justifyContent={'center'}
                    sx={{
                        borderRadius: 1,
                        background: red[400],
                        color: 'white',
                        maxWidth: '150px',
                        p: 0.5
                    }}
                >
                    <Typography
                        sx={{
                            mr: 1
                        }}
                    >
                        Manual
                    </Typography>
                    <BuildIcon sx={{ fontSize: 16 }} />
                </Stack>
            </Tooltip>
        );
    }

    renderMarcacaoAutomatica() {
        return (
            <Tooltip
                title={<Typography>Indicação automática de acordo com a classe processual.</Typography>}
                placement="top"
            >
                <Stack
                    direction={'row'}
                    alignItems={'center'}
                    alignContent={'center'}
                    justifyContent={'center'}
                    sx={{
                        borderRadius: 1,
                        background: blue[400],
                        color: 'white',
                        maxWidth: '150px',
                        p: 0.5
                    }}
                >
                    <Typography
                        sx={{
                            mr: 1
                        }}
                    >
                        Automática
                    </Typography>
                    <FlashAutoIcon sx={{ fontSize: 16 }} />
                </Stack>
            </Tooltip>
        );
    }

    getIconePermissao(permissao) {
        return permissao === true ? (
            <Tooltip title={<Typography>Permitir</Typography>}>
                <CheckCircleOutline color="success" />
            </Tooltip>
        ) : (
            <Tooltip title={<Typography>Não permitir</Typography>}>
                <Block color="error" />
            </Tooltip>
        );
    }

    renderListaProcessos() {
        return (
            <TableContainer>
                <Table
                    stickyHeader
                    aria-label="Tabela de processos da sessão"
                >
                    <TableHead style={{ maxHeight: '150px' }}>
                        <TableRow>
                            <TableCell></TableCell>
                            <TableCell
                                align="center"
                                sx={ESTILO_CABECALHO_TABELA}
                            >
                                Número do processo
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={ESTILO_CABECALHO_TABELA}
                            >
                                Sustentação oral eletrônica
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={ESTILO_CABECALHO_TABELA}
                            >
                                Pedido de destaque
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={{ ...ESTILO_CABECALHO_TABELA, borderRight: ESTILO_BORDA, width: '150px' }}
                            >
                                Indicação
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody style={{ overflowY: 'auto', maxHeight: '400px' }}>
                        {this.processosSelecionaveis.length > 0 ? (
                            this.processosSelecionaveis.map((registro) => {
                                return (
                                    <TableRow key={registro?.processo.numeroCompleto}>
                                        <TableCell
                                            align="center"
                                            sx={{ width: '50px', borderLeft: ESTILO_BORDA }}
                                        >
                                            {this.renderCheckbox(registro)}
                                        </TableCell>
                                        <TableCell
                                            align="left"
                                            sx={{
                                                borderLeft: ESTILO_BORDA
                                            }}
                                        >
                                            {registro?.processo.numeroCompleto}
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                borderLeft: ESTILO_BORDA,
                                                alignContent: 'center',
                                                width: '100px'
                                            }}
                                        >
                                            {this.getIconePermissao(
                                                registro?.processo?.permiteSustentacaoDetalhes
                                                    ?.marcacaoPermiteSustentacao ??
                                                    registro?.processo?.permiteSustentacao
                                            )}
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                borderLeft: ESTILO_BORDA,
                                                alignContent: 'center',
                                                width: '100px'
                                            }}
                                        >
                                            {this.getIconePermissao(
                                                registro?.processo?.permiteSustentacaoDetalhes
                                                    ?.marcacaoPermiteDestaque ?? registro?.processo?.permiteDestaque
                                            )}
                                        </TableCell>
                                        <TableCell
                                            align={'center'}
                                            sx={{
                                                borderLeft: ESTILO_BORDA,
                                                borderRight: ESTILO_BORDA,
                                                alignContent: 'center'
                                            }}
                                        >
                                            {registro?.processo?.permiteSustentacaoDetalhes?.hasOwnProperty(
                                                'marcacaoPermiteSustentacao'
                                            )
                                                ? this.renderMarcacaoManual(
                                                      registro?.processo.permiteSustentacaoDetalhes
                                                  )
                                                : this.renderMarcacaoAutomatica()}
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        ) : (
                            <TableRow>
                                <TableCell colSpan={4}>
                                    <Box sx={{ textAlign: 'center', p: 2 }}>Nenhum processo encontrado.</Box>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    }

    render() {
        return (
            <>
                <Tooltip
                    title={
                        <Typography>
                            {this.isJulgamentoExclusivamentePresencial
                                ? 'Essa sessão é exclusivamente presencial e não permite habilitação de permissão de inscrição para sustentação oral eletrônica e pedido de destaque'
                                : 'Controlar permissão de inscrição para sustentação oral eletrônica e pedido de destaque'}
                        </Typography>
                    }
                    placement="top-start"
                >
                    <Box>
                        <Button
                            disabled={this.isJulgamentoExclusivamentePresencial}
                            style={{
                                display: 'flex',
                                cursor: 'pointer',
                                color: 'white',
                                padding: '0px',
                                minWidth: 'auto',
                                margin: '8px'
                            }}
                            className="corBotaoAmarelo"
                            onClick={this.handleClickIcone}
                        >
                            <VoiceOverOff />
                        </Button>
                    </Box>
                </Tooltip>
                {this.dialogOpen && (
                    <Dialog
                        fullWidth
                        maxWidth="xl"
                        onClose={this.handleCloseDialog}
                        open={this.dialogOpen}
                        slotProps={{ paper: { style: { height: '100%' } } }}
                    >
                        <DialogTitle
                            id="simple-dialog-title"
                            style={{ padding: '16px 24px 0px' }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <h4 style={{ margin: '0px' }}>
                                        Controle de permissão de inscrição para sustentação oral eletrônica e pedido de
                                        destaque
                                    </h4>
                                </div>
                                <div style={{ margin: '0px 10px' }}>
                                    <NumeroProcesso
                                        dense
                                        tooltip="Número do processo a ser pesquisado na listagem"
                                        callbackOnChange={this.callbackNumeroProcesso}
                                        clickLimpar={this.limparProcessoPesquisa}
                                    />
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            margin: '10px',
                                            paddingTop: '5px'
                                        }}
                                    >
                                        <PainelFiltros />
                                    </div>
                                    <FiltroRapido id="filtroRapidoModalControlePermissaoSustentacao" />
                                </div>
                            </div>
                        </DialogTitle>
                        <OperacoesEmLoteSelecionar
                            processosSelecionaveis={this.processosSelecionaveis}
                            processos={this.processos}
                        />
                        <div
                            style={{ display: 'flex', flexGrow: '1', margin: '0px 20px', height: 'calc(100% - 250px)' }}
                        >
                            {this.renderListaProcessos()}
                        </div>
                        <DialogActions sx={{ display: 'flex', justifyContent: 'space-between', mx: 2 }}>
                            <Button
                                onClick={this.handleCloseDialog}
                                color="secondary"
                                variant="outlined"
                                endIcon={<Cancel />}
                            >
                                Fechar
                            </Button>
                            <Box
                                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}
                            >
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={this.permitirSustentacao}
                                            onChange={this.handleChangePermitirSustentacao}
                                        />
                                    }
                                    label="Permitir sustentação oral eletrônica"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={this.permitirDestaque}
                                            onChange={this.handleChangePermitirDestaque}
                                        />
                                    }
                                    label="Permitir pedido de destaque"
                                />
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 1
                                    }}
                                >
                                    <Button
                                        onClick={this.alterarPermissaoSustentacaoEmLote}
                                        variant="contained"
                                        endIcon={<Check />}
                                    >
                                        Alterar
                                    </Button>
                                </Box>
                            </Box>
                        </DialogActions>
                    </Dialog>
                )}
            </>
        );
    }
}

export default inject('stores')(observer(ModalControlePermissaoSustentacao));
